import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import uniq from 'lodash/uniq'
import isEqual from 'lodash/isEqual'

import { Trans } from '../../i18n'

import { useSearch } from '../../lib/use-search'

import { CsrfContext } from '../forms/Form'
import ListFavs from '../LocationPicker/ListFavs'
import ListNearby from '../LocationPicker/ListNearby'
import ListAll from '../LocationPicker/ListAll'

export const FAV_TYPE = {
  ADD: 1,
  REMOVE: 2
}

function LocationSelector({
  locations,
  onSelect,
  favable = true,
  disabledLocationIds = [],
  zoomable = true,
  mapLocations
}) {
  const {
    query,
    searchHandler,
    currentFavs,
    filteredFavs,
    filteredLocations
  } = useSearch()
  const csrfToken = useContext(CsrfContext)
  
  const [nearbyLocations, setNearbyLocations] = useState([])

  const locationIdsWithGeoJson = mapLocations
    ?.map(location => location.id)
    ?.sort((a, b) => a - b)

  const calcDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const λ1 = lon1 * Math.PI / 180;
    const λ2 = lon2 * Math.PI / 180;

    const x = (λ2 - λ1) * Math.cos((φ1 + φ2) / 2);
    const y = (φ2 - φ1);
    const d = Math.sqrt(x * x + y * y) * R;
    return d;
  }

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
      (position) => {
        const nearby = mapLocations.filter(location => {
          const distance = calcDistance(
            location.addressLatitude,
            location.addressLongitude,
            position.coords.latitude,
            position.coords.longitude
          )

          return distance < 10000 // 10km
        })
        
        const sortedNearby = nearby
          .map(location => ({
            ...location,
            city: locations[location.id].city,
            distance: calcDistance(
              location.addressLatitude,
              location.addressLongitude,
              position.coords.latitude,
              position.coords.longitude
            )
          }))
          .sort((a, b) => a.distance - b.distance)
        setNearbyLocations(sortedNearby)
      },
      (error) => {
        setNearbyLocations([])
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    ) 
    }
  }, [mapLocations])

  function handleFavChange(locationIds, { type }) {
    let method
    if (type === FAV_TYPE.ADD) method = 'POST'
    else if (type === FAV_TYPE.REMOVE) method = 'DELETE'
    else throw new Error('Unknown FAV_TYPE ' + type)

    // Optimistic UI: mark them as favorite before getting server response
    let newCurrentFavs = []
    if (type === FAV_TYPE.ADD) {
      newCurrentFavs = uniq([...currentFavs, ...locationIds])
    } else if (type === FAV_TYPE.REMOVE) {
      newCurrentFavs = currentFavs.filter(id => !locationIds.includes(id))
    }
    searchHandler({ newCurrentFavs })

    fetch('/locations/favorites', {
      method,
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ids: locationIds })
    })
      .then(res => res.json())
      .then(responseIds => {
        const responseIsSameAsCurrent = isEqual(
          responseIds.sort(),
          newCurrentFavs.sort()
        )
        if (responseIsSameAsCurrent) return

        searchHandler({ newCurrentFavs: responseIds })
      })
  }

  return (
    <React.Fragment>
      {nearbyLocations?.length > 0 && (
        <ListNarrowContainer>
          <ListNearby
            locations={nearbyLocations}
            onSelect={onSelect}
            favable={favable}
            zoomable={zoomable}
            disabledLocationIds={disabledLocationIds}
            locationIdsWithGeoJson={locationIdsWithGeoJson}
          />
        </ListNarrowContainer>
      )}
      {filteredFavs?.length > 0 && (
        <ListNarrowContainer>
          <ListFavs
            onFavChange={handleFavChange}
            locations={filteredFavs.map(id => locations[id])}
            onSelect={onSelect}
            favable={favable}
            zoomable={zoomable}
            disabledLocationIds={disabledLocationIds}
            locationIdsWithGeoJson={locationIdsWithGeoJson}
          />
        </ListNarrowContainer>
      )}
      <div>
        {filteredLocations.length ? (
          <ListAll
            groupedLocations={filteredLocations}
            onFavChange={handleFavChange}
            favLocationIds={filteredFavs}
            disabledLocationIds={disabledLocationIds}
            onSelect={onSelect}
            favable={favable}
            zoomable={zoomable}
            locationIdsWithGeoJson={locationIdsWithGeoJson}
          />
        ) : (
          <NoResultsText>
            <Trans id="locations.no_results" values={{ query }} escape />
          </NoResultsText>
        )}
      </div>
    </React.Fragment>
  )
}

LocationSelector.propTypes = {
  locations: PropTypes.object.isRequired,
  favable: PropTypes.bool,
  onSelect: PropTypes.func,
  disabledLocationIds: PropTypes.arrayOf(PropTypes.number),
  zoomable: PropTypes.bool,
  mapLocations: PropTypes.arrayOf(PropTypes.object)
}

const NoResultsText = styled.div`
  padding: 2rem;
  font-size: 2rem;
  font-style: italic;
  text-align: center;
`

const ListNarrowContainer = styled.div`
  margin-top: 1.5rem;

  ${p => p.theme.breakpoints.phone} {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
`

export function normalizeQuery(searchString) {
  return searchString.trim().toLowerCase()
}

export default LocationSelector
